import React from 'react';
import { STYLE_CFG, LOGO_SLOT_W, LOGO_SLOT_H, resolveHeaderColors, fc, fd, PositionedLogo } from './InvoiceLetterhead';
import { buildFirmAddress } from '../utils/firmAddress';

// ── Receipt / Quotation / Credit Note letterheads ───────────────────────────
// Same visual recipe as InvoiceLetterhead.js (classic/corporate/bold header,
// header_style navy/primary/gradient, PositionedLogo, accent separator, a
// colored "label pill" for the from/to block, table, footer band) so every
// document type in the Finance module — and its Billing Settings live preview
// — looks like one family instead of four unrelated designs. Only the doc
// title, badge text, party-block labels, table columns and footer content
// differ per document.

function LetterheadHeader({ s, cfg, templateStyle, previewColor, docTitle, badgeText, metaRows }) {
  const isCorporate = templateStyle === 'corporate';
  const isBold = templateStyle === 'bold';
  const firmAddr = buildFirmAddress(s);

  if (isCorporate) {
    return (
      <div style={{ background: '#fff', borderBottom: `3px solid ${previewColor}`, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 0 }}>
        <div style={{ width: 5, background: previewColor, borderRadius: '3px 0 0 3px', alignSelf: 'stretch', marginRight: 16, flexShrink: 0 }}></div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
            {s.logo_url
              ? <PositionedLogo logoUrl={s.logo_url} x={s.logo_x} y={s.logo_y} scale={s.logo_scale} />
              : <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '40px', padding: '6px 14px', background: `${previewColor}18`, border: `2px solid ${previewColor}30`, borderRadius: 6, alignSelf: 'flex-start' }}>
                  <span style={{ color: previewColor, fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{s.firm_name || 'LCCA'}</span>
                </div>}
            <div>
              <div style={{ fontSize: '15px', fontWeight: '800', color: previewColor, letterSpacing: '0.2px', lineHeight: 1.2 }}>{s.firm_name || 'CHANAKYA X'}</div>
              <div style={{ fontSize: '9px', color: '#666', marginTop: 4, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{firmAddr}{'\n'}{s.firm_phone_num || s.firm_mobile ? `Mobile: ${s.firm_phone_num || s.firm_mobile}` : ''}{s.firm_email ? ` | ${s.firm_email}` : ''}</div>
              {s.firm_gstin && <div style={{ marginTop: 3, fontSize: '9px', color: '#888' }}>GSTIN: <strong style={{ color: previewColor }}>{s.firm_gstin}</strong></div>}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right', paddingLeft: 18, borderLeft: '1px solid #f0f0f0', flexShrink: 0 }}>
          <div style={{ fontSize: '20px', fontWeight: '800', color: previewColor, letterSpacing: '1px' }}>{docTitle}</div>
          <div style={{ fontSize: '8px', background: previewColor, color: '#fff', padding: '2px 10px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, marginTop: 5, borderRadius: 3, letterSpacing: '0.5px' }}>{badgeText}</div>
          <div style={{ marginTop: 8, fontSize: '10px', color: '#555', lineHeight: 2 }}>
            {metaRows.map((r, i) => <div key={i}><span style={{ color: '#999' }}>{r.label} </span><strong style={{ color: r.danger ? '#dc2626' : '#111' }}>{r.value}</strong></div>)}
          </div>
        </div>
      </div>
    );
  }

  if (isBold) {
    return (
      <div style={{ background: cfg.headerBg }}>
        <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '55%' }}>
            {s.logo_url
              ? <PositionedLogo logoUrl={s.logo_url} x={s.logo_x} y={s.logo_y} scale={s.logo_scale} />
              : <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '40px', padding: '6px 14px', background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.25)', borderRadius: 6, alignSelf: 'flex-start' }}>
                  <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{s.firm_name || 'LCCA'}</span>
                </div>}
            <div>
              <div style={{ fontSize: '15px', fontWeight: '800', color: '#fff', letterSpacing: '0.4px' }}>{s.firm_name || 'CHANAKYA X'}</div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.7)', marginTop: 4, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{firmAddr}{'\n'}{s.firm_phone_num || s.firm_mobile || ''}{s.firm_email ? ` | ${s.firm_email}` : ''}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#f97316', letterSpacing: '1px' }}>{docTitle}</div>
            <div style={{ fontSize: '8px', border: '1px solid rgba(249,115,22,0.6)', color: '#f97316', padding: '2px 10px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, marginTop: 5, borderRadius: 3, letterSpacing: '0.5px' }}>{badgeText}</div>
            <div style={{ marginTop: 8, fontSize: '10px', color: 'rgba(255,255,255,0.85)', lineHeight: 2 }}>
              {metaRows.map((r, i) => <div key={i}><span style={{ opacity: 0.7 }}>{r.label} </span><strong style={{ color: r.danger ? '#fbbf24' : undefined }}>{r.value}</strong></div>)}
            </div>
          </div>
        </div>
        <div style={{ height: 4, background: '#f97316' }}></div>
      </div>
    );
  }

  return (
    <div style={{ background: cfg.headerBg, padding: '20px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '55%' }}>
        {s.logo_url
          ? <PositionedLogo logoUrl={s.logo_url} x={s.logo_x} y={s.logo_y} scale={s.logo_scale} />
          : <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '40px', padding: '6px 14px', background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 4, alignSelf: 'flex-start' }}>
              <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{s.firm_name || 'LCCA'}</span>
            </div>}
        <div>
          <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#fff', letterSpacing: '0.3px', lineHeight: 1.2 }}>{s.firm_name || 'CHANAKYA X'}</div>
          <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.82)', marginTop: 4, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{firmAddr}{'\n'}{s.firm_phone_num || s.firm_mobile ? `Mobile: ${s.firm_phone_num || s.firm_mobile}` : ''}{s.firm_email ? ` | ${s.firm_email}` : ''}</div>
          {s.firm_gstin && <div style={{ marginTop: 3, fontSize: '9px', color: 'rgba(255,255,255,0.6)' }}>GSTIN: <strong style={{ color: 'rgba(255,255,255,0.9)' }}>{s.firm_gstin}</strong></div>}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff', letterSpacing: '1.5px' }}>{docTitle}</div>
        <div style={{ fontSize: '8px', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, marginTop: 4, borderRadius: 2 }}>{badgeText}</div>
        <div style={{ marginTop: 8, fontSize: '10px', color: 'rgba(255,255,255,0.9)', lineHeight: 2 }}>
          {metaRows.map((r, i) => <div key={i}><span style={{ opacity: 0.7 }}>{r.label} </span><strong>{r.value}</strong></div>)}
        </div>
      </div>
    </div>
  );
}

function Separator({ templateStyle, previewColor }) {
  if (templateStyle === 'classic') return <div style={{ display: 'flex', height: '5px' }}><div style={{ flex: 1, background: previewColor }}></div><div style={{ flex: 1, background: '#c8a951' }}></div><div style={{ flex: 1, background: '#e8d9a0' }}></div></div>;
  if (templateStyle === 'corporate') return <div style={{ height: '2px', background: '#0f1f45' }}></div>;
  return null;
}

// The "BILL TO"-style colored pill block, reused for every document's
// from/to party bar — left side always carries the party name, right side
// always carries the small reference key/value rows.
function PartyBar({ theadColors, leftLabel, leftName, leftSub, rightLabel, rightRows }) {
  return (
    <div style={{ padding: '12px 24px', borderBottom: '1px solid #eee' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '9px', fontWeight: 'bold', background: theadColors.bg, color: theadColors.text, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, borderRadius: '2px', marginBottom: '7px', letterSpacing: '0.4px' }}>{leftLabel}</div>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1a1a1a' }}>{leftName || '—'}</div>
          {leftSub && <div style={{ fontSize: '10px', color: '#555', lineHeight: 1.7, marginTop: '2px' }}>{leftSub}</div>}
        </div>
        <div>
          <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '7px' }}>{rightLabel}</div>
          <div style={{ fontSize: '10px', color: '#555', lineHeight: 1.9 }}>
            {rightRows.filter(r => r.value).map((r, i) => (
              <div key={i}><span style={{ color: '#888' }}>{r.label}: </span><strong style={{ color: '#1a1a1a' }}>{r.value}</strong></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Signature({ s }) {
  return (
    <div style={{ textAlign: 'center', fontSize: '10px' }}>
      {s.signature_url && <img src={s.signature_url} alt="Sig" style={{ maxHeight: '36px', maxWidth: '90px', display: 'block', margin: '0 auto 4px' }} />}
      <div style={{ width: '90px', height: '32px', borderBottom: '1px solid #999', margin: '0 auto 4px' }}></div>
      <div style={{ fontWeight: 'bold', color: '#333', fontSize: '10px' }}>{s.signature_name || 'Authorised Signature'}</div>
      <div style={{ color: '#555', fontSize: '8.5px', marginTop: '1px' }}>{s.signature_designation ? s.signature_designation + ' | ' : ''}For {s.firm_name || 'Your Firm Name'}</div>
    </div>
  );
}

function shellStyle(font) {
  return { background: '#fff', fontFamily: font, color: '#1a1a1a', fontSize: '11px', width: '794px', position: 'relative' };
}

// ─────────────────────────────────────────────────────────────────────────
// RECEIPT
// ─────────────────────────────────────────────────────────────────────────
export function ReceiptLetterhead({ settings = {}, receipt = {}, qrDataUrl = '' }) {
  const s = settings;
  const templateStyle = s.template_style || 'corporate';
  const cfg = STYLE_CFG[templateStyle] || STYLE_CFG.corporate;
  const previewColor = s.primary_color || '#0f1f45';
  const font = s.invoice_font || 'Plus Jakarta Sans';
  const theadColors = resolveHeaderColors(cfg, previewColor, s.header_style);
  const docTitle = s.receipt_heading || 'Receipt';

  const settlements = receipt.settlements || [];
  const clientAddrParts = [receipt.client_address, receipt.client_city, receipt.client_state, receipt.client_pincode].filter(Boolean);
  // Prefer the firm's own configured Payment Mode name (resolved by the
  // caller, which has the Payment Modes list) over a generic humanised
  // version of the internal key this component alone can't look up.
  const modeLabel = receipt.mode_label || (receipt.mode ? receipt.mode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—');

  const totalBilled = settlements.reduce((sum, r) => sum + Number(r.invoice_amount ?? r.amount ?? 0), 0);
  const showLedger = s.show_receipt_ledger !== false && (settlements.length > 0 || receipt.balance_after != null);

  return (
    <div style={shellStyle(font)}>
      <LetterheadHeader s={s} cfg={cfg} templateStyle={templateStyle} previewColor={previewColor}
        docTitle={docTitle} badgeText="PAYMENT RECEIVED"
        metaRows={[
          { label: 'Receipt No.', value: receipt.receipt_number || '—' },
          { label: 'Date', value: fd(receipt.payment_date) },
          { label: 'Mode', value: modeLabel },
        ]} />
      <Separator templateStyle={templateStyle} previewColor={previewColor} />

      <PartyBar theadColors={theadColors}
        leftLabel="RECEIVED FROM" leftName={receipt.client_name}
        leftSub={clientAddrParts.join(', ')}
        rightLabel="Reference"
        rightRows={[
          { label: 'UTR / Ref.', value: receipt.reference_number },
          { label: 'Against', value: settlements.map(r => r.invoice_number).filter(Boolean).join(', ') || receipt.invoice_number },
        ]} />

      <div style={{ padding: '0 24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr style={{ background: theadColors.bg, color: theadColors.text }}>
              <th style={{ padding: '7px 8px', textAlign: 'center', width: '26px' }}>#</th>
              <th style={{ padding: '7px 8px', textAlign: 'left' }}>Settled Against</th>
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '100px' }}>Invoice Total</th>
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '100px' }}>Amount Received</th>
            </tr>
          </thead>
          <tbody>
            {(settlements.length ? settlements : [{ invoice_number: receipt.invoice_number, invoice_amount: receipt.invoice_total_amount, amount: receipt.received_amount }]).map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                <td style={{ padding: '8px 8px', textAlign: 'center', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{i + 1}</td>
                <td style={{ padding: '8px 8px', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>
                  <div style={{ fontWeight: 'bold', color: previewColor, fontSize: '10.5px' }}>{row.invoice_number ? `${row.invoice_number}` : (receipt.notes || `Payment received via ${modeLabel}`)}</div>
                  {s.show_receipt_services !== false && row.service_names && (
                    <div style={{ color: '#555', fontSize: '9.5px', lineHeight: 1.6, marginTop: '2px' }}>For: {row.service_names}</div>
                  )}
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{row.invoice_amount != null ? fc(row.invoice_amount) : '—'}</td>
                <td style={{ padding: '8px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{fc(row.amount ?? receipt.received_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: s.show_invoice_qr !== false && s.upi_id && Number(receipt.balance_after) > 0 ? '1.2fr 0.8fr 1fr' : '1fr 1fr', gap: 0, padding: '12px 24px 0', borderTop: '1px solid #eee' }}>
        <div style={{ paddingRight: '12px', borderRight: '1px solid #eee', fontSize: '10px' }}>
          <div style={{ fontWeight: 'bold', color: previewColor, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '9px' }}>{showLedger ? 'Ledger Position' : 'Notes'}</div>
          {showLedger ? (
            <div style={{ lineHeight: 1.9, color: '#444' }}>
              {totalBilled > 0 && <div><span style={{ color: '#888', display: 'inline-block', width: '110px' }}>Total Billed:</span>{fc(totalBilled)}</div>}
              <div><span style={{ color: '#888', display: 'inline-block', width: '110px' }}>Received to Date:</span>{fc(receipt.received_amount)}</div>
              {receipt.balance_after != null && <div><span style={{ color: '#888', display: 'inline-block', width: '110px' }}>Balance:</span><strong style={{ color: Number(receipt.balance_after) > 0 ? '#dc2626' : '#16a34a' }}>{fc(receipt.balance_after)}</strong></div>}
            </div>
          ) : (
            <div style={{ color: '#555', lineHeight: 1.7 }}>{receipt.notes || '—'}</div>
          )}
        </div>
        {s.show_invoice_qr !== false && s.upi_id && Number(receipt.balance_after) > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 12px', borderRight: '1px solid #eee' }}>
            {qrDataUrl && <img src={qrDataUrl} alt="UPI QR" style={{ width: '80px', height: '80px', borderRadius: '4px', display: 'block' }} />}
            <div style={{ marginTop: '6px', textAlign: 'center' }}>
              <div style={{ fontSize: '8px', fontWeight: 'bold', color: previewColor }}>Scan to Pay Balance</div>
              <div style={{ fontSize: '8px', color: '#777', marginTop: '2px' }}>{s.upi_id}</div>
            </div>
          </div>
        )}
        <div style={{ paddingLeft: '14px', fontSize: '11px' }}>
          <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: theadColors.bg, color: theadColors.text, fontWeight: 'bold', fontSize: '12px' }}><span>Amount Received</span><span>{fc(receipt.received_amount)}</span></div>
          </div>
        </div>
      </div>

      <div style={{ margin: '10px 24px 0', padding: '10px 0 18px', borderTop: '1px solid #eee', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px' }}>
        <div style={{ fontSize: '9px', color: '#555', lineHeight: 1.8 }}>
          {s.receipt_footer_html
            ? <div dangerouslySetInnerHTML={{ __html: s.receipt_footer_html }} />
            : <div>This is a computer-generated receipt confirming payment of {fc(receipt.received_amount)}.</div>}
        </div>
        <Signature s={s} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// QUOTATION
// ─────────────────────────────────────────────────────────────────────────
export function QuotationLetterhead({ settings = {}, quote = {}, items = null }) {
  const s = settings;
  const templateStyle = s.template_style || 'corporate';
  const cfg = STYLE_CFG[templateStyle] || STYLE_CFG.corporate;
  const previewColor = s.primary_color || '#0f1f45';
  const font = s.invoice_font || 'Plus Jakarta Sans';
  const theadColors = resolveHeaderColors(cfg, previewColor, s.header_style);
  const docTitle = s.quotation_heading || 'Quotation';

  const rows = items || quote.items || [];
  const qItemAmount = (it) => it.amount != null ? Number(it.amount) : Number(it.quantity || 1) * Number(it.rate || 0);
  const qItemDiscount = (it) => {
    if (it.discount != null) return Number(it.discount);
    const amt = qItemAmount(it);
    if (it.discount_type === 'percentage') return amt * Number(it.discount_value || 0) / 100;
    if (it.discount_type === 'fixed') return Number(it.discount_value || 0);
    return 0;
  };
  const showDiscountCol = rows.some(r => qItemDiscount(r) > 0);
  const subtotal = rows.reduce((sum, r) => sum + qItemAmount(r), 0);
  const discountTotal = rows.reduce((sum, r) => sum + qItemDiscount(r), 0);
  // Professional fees carry GST; Government fees are paid on the client's behalf (no GST unless ticked).
  const truthyG = (v) => v === true || v === 'true';
  const isGovtRow = (r) => r.charge_type === 'government';
  const isPassRow = (r) => isGovtRow(r) && !truthyG(r.gst_applies);
  const rowNet = (r) => qItemAmount(r) - qItemDiscount(r);
  const gstRegistered = s.is_gst_registered !== false;
  const hasGovt = rows.some(isGovtRow);
  const taxableTotal = rows.filter(r => !isPassRow(r)).reduce((a, r) => a + rowNet(r), 0);
  const passTotal = rows.filter(isPassRow).reduce((a, r) => a + rowNet(r), 0);
  const gstAmount = gstRegistered ? taxableTotal * 0.18 : 0;
  const total = rows.length ? taxableTotal + gstAmount + passTotal : Number(quote.total_amount ?? quote.amount ?? (subtotal - discountTotal));

  return (
    <div style={shellStyle(font)}>
      <LetterheadHeader s={s} cfg={cfg} templateStyle={templateStyle} previewColor={previewColor}
        docTitle={docTitle} badgeText={quote.valid_till ? `VALID TILL ${fd(quote.valid_till)}` : 'PROPOSAL'}
        metaRows={[
          { label: 'Quote No.', value: quote.quote_number || '—' },
          { label: 'Date', value: fd(quote.created_at || quote.quote_date) },
        ]} />
      <Separator templateStyle={templateStyle} previewColor={previewColor} />

      <PartyBar theadColors={theadColors}
        leftLabel="PREPARED FOR" leftName={quote.client_name}
        leftSub={quote.lead_ref ? `Lead ref. ${quote.lead_ref} · ${(quote.source || 'crm') === 'direct' ? 'Direct' : 'CRM Lead'}` : ((quote.source || 'crm') === 'direct' ? 'Direct' : 'CRM Lead')}
        rightLabel="Firm Details"
        rightRows={[
          { label: 'GSTIN', value: s.firm_gstin || '—' },
          { label: 'PAN', value: s.firm_pan || '—' },
        ]} />

      <div style={{ padding: '0 24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr style={{ background: theadColors.bg, color: theadColors.text }}>
              <th style={{ padding: '7px 8px', textAlign: 'center', width: '26px' }}>#</th>
              <th style={{ padding: '7px 8px', textAlign: 'left' }}>Proposed Service</th>
              {showDiscountCol && <th style={{ padding: '7px 8px', textAlign: 'right', width: '65px' }}>Discount</th>}
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '80px' }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const list = rows.length ? rows : [{ particular: 'Service', description: '', amount: total }];
              const renderRow = (row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                <td style={{ padding: '8px 8px', textAlign: 'center', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{i + 1}</td>
                <td style={{ padding: '8px 8px', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>
                  <div style={{ fontWeight: 'bold', color: previewColor, fontSize: '10.5px' }}>{row.description || row.particular || 'Service'}</div>
                </td>
                {showDiscountCol && <td style={{ padding: '8px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee', color: '#e8502a' }}>{qItemDiscount(row) > 0 ? fc(qItemDiscount(row)) : '—'}</td>}
                <td style={{ padding: '8px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{fc(qItemAmount(row) - qItemDiscount(row))}</td>
              </tr>
              );
              if (!hasGovt) return list.map(renderRow);
              let k = 0;
              const sec = (label, sub) => (
                <tr key={label}><td colSpan={showDiscountCol ? 4 : 3} style={{ padding: '7px 8px 4px', fontSize: '10px', fontWeight: 'bold', color: previewColor, borderBottom: '1px solid #eee' }}>{label}{sub && <span style={{ fontWeight: 'normal', color: '#888' }}> — {sub}</span>}</td></tr>
              );
              return [
                sec('A. Professional Fees'),
                ...list.filter(r => !isGovtRow(r)).map(r => renderRow(r, k++)),
                sec('B. Government Fees', 'at actuals, paid on your behalf'),
                ...list.filter(isGovtRow).map(r => renderRow(r, k++)),
              ];
            })()}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 0, padding: '12px 24px 0', borderTop: '1px solid #eee' }}>
        <div style={{ paddingRight: '16px', fontSize: '9px', color: '#555', lineHeight: 1.8, alignSelf: 'flex-end' }}>
          <div style={{ fontWeight: 'bold', color: previewColor, textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '9px', marginBottom: 6 }}>Terms &amp; Conditions</div>
          {s.quotation_footer_html
            ? <div dangerouslySetInnerHTML={{ __html: s.quotation_footer_html }} />
            : <>
                <div>1. This quotation is valid till the date stated above.</div>
                <div>2. 50% advance required to commence work.</div>
                <div>3. GST will be charged as applicable.</div>
              </>}
        </div>
        <div style={{ paddingLeft: '14px', fontSize: '11px' }}>
          <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden' }}>
            {hasGovt ? (<>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>Professional fees</span><span>{fc(taxableTotal)}</span></div>
              {gstRegistered && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>GST (18%)</span><span>{fc(gstAmount)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>Government fees (no GST)</span><span>{fc(passTotal)}</span></div>
            </>) : (<>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>Subtotal</span><span>{fc(subtotal)}</span></div>
              {discountTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee', color: '#e8502a' }}><span>Discount</span><span>— {fc(discountTotal)}</span></div>}
              {gstRegistered && gstAmount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>GST (18%)</span><span>{fc(gstAmount)}</span></div>}
            </>)}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: theadColors.bg, color: theadColors.text, fontWeight: 'bold', fontSize: '12px' }}><span>Estimated Total</span><span>{fc(total)}</span></div>
          </div>
        </div>
      </div>
      <div style={{ padding: '16px 24px 24px' }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CLIENT LEDGER — a Tally-style "Statement of Account": every invoice and
// receipt for one client, in date order, with a running balance per row.
// `ledger` is the { entries, closing_balance, client } shape routes/
// invoiceRoutes.js's GET /ledger/:clientId returns, entries already sorted
// and balanced by the caller (Clients.js's ClientLedgerInline) — this
// component only renders what it's given.
// ─────────────────────────────────────────────────────────────────────────
export function ClientLedgerLetterhead({ settings = {}, ledger = {}, asOfDate = null }) {
  const s = settings;
  const templateStyle = s.template_style || 'corporate';
  const cfg = STYLE_CFG[templateStyle] || STYLE_CFG.corporate;
  const previewColor = s.primary_color || '#0f1f45';
  const font = s.invoice_font || 'Plus Jakarta Sans';
  const theadColors = resolveHeaderColors(cfg, previewColor, s.header_style);
  const docTitle = 'Statement of Account';

  const entries = ledger.entries || [];
  const client = ledger.client || {};
  const closingBalance = Number(ledger.closing_balance || 0);
  const totalInvoiced = entries.filter(e => e.type === 'Invoice').reduce((sum, e) => sum + Number(e.debit || 0), 0);
  const totalReceived = entries.filter(e => e.type === 'Receipt' || e.type === 'Payment').reduce((sum, e) => sum + Number(e.credit || 0), 0);
  const clientAddrParts = [client.address, client.city, client.state, client.pincode].filter(Boolean);
  const balLabel = closingBalance === 0 ? 'Settled' : closingBalance > 0 ? 'Receivable' : 'Advance';
  const balColor = closingBalance === 0 ? '#555' : closingBalance > 0 ? '#16a34a' : '#dc2626';

  return (
    <div style={shellStyle(font)}>
      <LetterheadHeader s={s} cfg={cfg} templateStyle={templateStyle} previewColor={previewColor}
        docTitle={docTitle} badgeText={`AS ON ${fd(asOfDate || new Date())}`}
        metaRows={[
          { label: 'File No.', value: client.file_number || '—' },
          { label: 'PAN', value: client.pan || '—' },
        ]} />
      <Separator templateStyle={templateStyle} previewColor={previewColor} />

      <PartyBar theadColors={theadColors}
        leftLabel="STATEMENT FOR" leftName={client.client_name}
        leftSub={clientAddrParts.join(', ')}
        rightLabel="Account Summary"
        rightRows={[
          { label: 'Total Invoiced', value: fc(totalInvoiced) },
          { label: 'Total Received', value: fc(totalReceived) },
          { label: `Closing (${balLabel})`, value: fc(Math.abs(closingBalance)) },
        ]} />

      <div style={{ padding: '0 24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr style={{ background: theadColors.bg, color: theadColors.text }}>
              <th style={{ padding: '7px 8px', textAlign: 'left', width: '68px' }}>Date</th>
              <th style={{ padding: '7px 8px', textAlign: 'left', width: '70px' }}>Type</th>
              <th style={{ padding: '7px 8px', textAlign: 'left' }}>Reference No.</th>
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '85px' }}>Debit (₹)</th>
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '85px' }}>Credit (₹)</th>
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '95px' }}>Balance (₹)</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '18px 8px', textAlign: 'center', color: '#999', borderBottom: '1px solid #eee' }}>No transactions recorded</td></tr>
            ) : entries.map((e, i) => {
              const eBal = Number(e.balance || 0);
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                  <td style={{ padding: '7px 8px', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{fd(e.date)}</td>
                  <td style={{ padding: '7px 8px', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{e.type}</td>
                  <td style={{ padding: '7px 8px', verticalAlign: 'top', borderBottom: '1px solid #eee', fontWeight: 'bold', color: previewColor }}>{e.reference || '—'}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{Number(e.debit) > 0 ? fc(e.debit) : '—'}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{Number(e.credit) > 0 ? fc(e.credit) : '—'}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee', fontWeight: 'bold', color: eBal === 0 ? '#555' : eBal > 0 ? '#dc2626' : '#16a34a' }}>
                    {eBal === 0 ? 'NIL' : `${fc(Math.abs(eBal))} ${eBal > 0 ? 'Dr' : 'Cr'}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 0, padding: '12px 24px 0', borderTop: '1px solid #eee' }}>
        <div style={{ paddingRight: '16px', fontSize: '9px', color: '#555', lineHeight: 1.8, alignSelf: 'flex-end' }}>
          This is a computer-generated statement reflecting all invoices and receipts recorded for this client as on {fd(asOfDate || new Date())}.
        </div>
        <div style={{ paddingLeft: '14px', fontSize: '11px' }}>
          <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: closingBalance === 0 ? theadColors.bg : (closingBalance > 0 ? '#16a34a' : '#dc2626'), color: '#fff', fontWeight: 'bold', fontSize: '12px' }}>
              <span>{closingBalance === 0 ? 'Closing Balance' : `Closing — ${balLabel}`}</span>
              <span>{closingBalance === 0 ? 'NIL' : fc(Math.abs(closingBalance))}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ margin: '10px 24px 0', padding: '10px 0 18px', borderTop: '1px solid #eee', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px' }}>
        <div />
        <Signature s={s} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CREDIT NOTE
// ─────────────────────────────────────────────────────────────────────────
export function CreditNoteLetterhead({ settings = {}, creditNote = {}, items = null }) {
  const s = settings;
  const templateStyle = s.template_style || 'corporate';
  const cfg = STYLE_CFG[templateStyle] || STYLE_CFG.corporate;
  const previewColor = s.primary_color || '#0f1f45';
  const font = s.invoice_font || 'Plus Jakarta Sans';
  const theadColors = resolveHeaderColors(cfg, previewColor, s.header_style);
  const docTitle = s.credit_note_heading || 'Credit Note';

  const rows = items || creditNote.items || [];
  const clientAddrParts = [creditNote.client_address, creditNote.client_city, creditNote.client_state, creditNote.client_pincode].filter(Boolean);

  return (
    <div style={shellStyle(font)}>
      <LetterheadHeader s={s} cfg={cfg} templateStyle={templateStyle} previewColor={previewColor}
        docTitle={docTitle} badgeText="ORIGINAL"
        metaRows={[
          { label: 'CN No.', value: creditNote.credit_note_number || '—' },
          { label: 'Date', value: fd(creditNote.credit_date) },
        ]} />
      <Separator templateStyle={templateStyle} previewColor={previewColor} />

      <PartyBar theadColors={theadColors}
        leftLabel="CREDIT ISSUED TO" leftName={creditNote.client_name}
        leftSub={clientAddrParts.join(', ')}
        rightLabel="Against Invoice"
        rightRows={[
          { label: 'Invoice', value: creditNote.original_invoice_number },
          { label: 'Reason', value: creditNote.reason },
        ]} />

      <div style={{ padding: '0 24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr style={{ background: theadColors.bg, color: theadColors.text }}>
              <th style={{ padding: '7px 8px', textAlign: 'center', width: '26px' }}>#</th>
              <th style={{ padding: '7px 8px', textAlign: 'left' }}>Particular</th>
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '80px' }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {(rows.length ? rows : [{ particular: creditNote.reason || 'Credit', amount: creditNote.total_amount }]).map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                <td style={{ padding: '8px 8px', textAlign: 'center', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{i + 1}</td>
                <td style={{ padding: '8px 8px', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>
                  <div style={{ fontWeight: 'bold', color: previewColor, fontSize: '10.5px' }}>{row.particular}</div>
                  {row.description && <div style={{ fontSize: '9px', color: '#555', lineHeight: 1.6, marginTop: '3px' }}>{row.description}</div>}
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{fc(row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 0, padding: '12px 24px 0', borderTop: '1px solid #eee' }}>
        <div style={{ paddingRight: '16px', fontSize: '9px', color: '#555', lineHeight: 1.8, alignSelf: 'flex-end' }}>
          Issued to correct invoice {creditNote.original_invoice_number || ''}{creditNote.reason ? ` — ${creditNote.reason}.` : '.'}
        </div>
        <div style={{ paddingLeft: '14px', fontSize: '11px' }}>
          <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: theadColors.bg, color: theadColors.text, fontWeight: 'bold', fontSize: '12px' }}><span>Credit Amount</span><span>{fc(creditNote.total_amount)}</span></div>
          </div>
        </div>
      </div>
      <div style={{ padding: '16px 24px 24px' }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// STATEMENT OF PROFIT & LOSS (Income Statement)
// ─────────────────────────────────────────────────────────────────────────
// Same firm-header/letterhead family as the other four documents, but the
// body is a proper P&L statement (Income / Expenditure / Net Profit) rather
// than a line-item bill — so it reuses LetterheadHeader + Separator + fc/fd
// for visual consistency, and builds its own section layout below that.
export function IncomeStatementLetterhead({ settings = {}, statement = {}, period = {} }) {
  const s = settings;
  const templateStyle = s.template_style || 'corporate';
  const cfg = STYLE_CFG[templateStyle] || STYLE_CFG.corporate;
  const previewColor = s.primary_color || '#0f1f45';
  const font = s.invoice_font || 'Plus Jakarta Sans';
  const theadColors = resolveHeaderColors(cfg, previewColor, s.header_style);

  const income = statement.income || {};
  const expenses = statement.expenses || {};
  const byCategory = expenses.by_category || [];
  const netProfit = Number(statement.net_profit || 0);
  const margin = Number(statement.margin || 0);

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: '9px', fontWeight: 'bold', background: theadColors.bg, color: theadColors.text, padding: '3px 10px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, borderRadius: '2px', letterSpacing: '0.5px' }}>{children}</div>
  );

  const Row = ({ label, value, bold, indent, negative }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: bold ? '8px 10px' : '6px 10px 6px', paddingLeft: indent ? 22 : 10, borderBottom: '0.5px solid #eee', fontWeight: bold ? 'bold' : 'normal', fontSize: bold ? '11px' : '10px' }}>
      <span style={{ color: bold ? '#1a1a1a' : '#555' }}>{label}</span>
      <span style={{ color: negative ? '#dc2626' : (bold ? '#1a1a1a' : '#333') }}>{negative ? '— ' : ''}{fc(value)}</span>
    </div>
  );

  return (
    <div style={shellStyle(font)}>
      <LetterheadHeader s={s} cfg={cfg} templateStyle={templateStyle} previewColor={previewColor}
        docTitle="Statement of Profit & Loss" badgeText="MANAGEMENT ACCOUNTS — NOT FOR STATUTORY FILING"
        metaRows={[
          { label: 'Period From', value: fd(period.from) },
          { label: 'Period To', value: fd(period.to) },
          { label: 'Generated', value: fd(new Date()) },
        ]} />
      <Separator templateStyle={templateStyle} previewColor={previewColor} />

      <div style={{ padding: '18px 24px 0' }}>
        <SectionLabel>INCOME</SectionLabel>
        <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden', marginTop: '8px' }}>
          <Row label="Invoiced Revenue (accrual, invoice date)" value={income.invoiced_revenue} indent />
          <Row label="Other Income" value={income.other_income} indent />
          <Row label="Total Income (A)" value={income.total} bold />
        </div>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        <SectionLabel>EXPENDITURE</SectionLabel>
        <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden', marginTop: '8px' }}>
          {byCategory.length === 0 ? (
            <Row label="No expenses recorded in this period" value={0} indent />
          ) : byCategory.map((c, i) => (
            <Row key={c.value || i} label={`${c.category}${c.type === 'direct' ? ' (Direct — Client/Task)' : ''}`} value={c.amount} indent />
          ))}
          <Row label="Total Expenditure (B)" value={expenses.total} bold />
        </div>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        <div style={{ border: `1px solid ${previewColor}`, borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: theadColors.bg, color: theadColors.text, fontWeight: 'bold', fontSize: '13px' }}>
            <span>Net Profit (A − B)</span>
            <span>{netProfit < 0 ? '— ' : ''}{fc(Math.abs(netProfit))}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 12px', fontSize: '10px', color: '#666', background: '#f9f9f9' }}>
            <span>Profit Margin</span>
            <span style={{ fontWeight: 'bold', color: netProfit >= 0 ? '#16a34a' : '#dc2626' }}>{margin.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      <div style={{ margin: '14px 24px 0', padding: '8px 10px', borderRadius: '4px', fontSize: '9px', color: '#888', background: '#f8f9fb', borderLeft: `3px solid ${previewColor}` }}>
        Prepared for internal management review from books maintained in {s.firm_name || 'the firm'}'s practice software. Income is recognised on an accrual basis (invoice date); expenses are recognised on their recorded date. This is not a statutory financial statement.
      </div>

      <div style={{ margin: '18px 24px 0', padding: '10px 0 18px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
        <Signature s={s} />
      </div>
    </div>
  );
}
